import { html, type Html, Page } from 'rune-ts';
import klass from './CurationList.module.scss';
import { LayoutView } from '../../components/cells/Layout/LayoutView';
import { footer_links } from '../../components/cells/Footer/constant';
import { updateUrlParams } from '../../../../../modules/Util/S/Function/util';
import { CurationListItemView } from '../../components/cells/CurationListItem/CurationListItem';
import { Pagination, PaginationChangeEvent } from '../../components/cells/Pagination/Pagination';
import { CurationListItem } from '../../../features/Curation/type';
import { BaseLayoutOptions } from '../../../features/Core/service';
import { ProductCardViewData } from '../../../features/ProductList/type';
import { FloatingButton_recent_product } from '../../components/cells/FloatingBox/FloatingButtons/FloatingButton_recent_product';
import { FloatingBox } from '../../components/cells/FloatingBox/FloatingBox';
import { CoreUiS } from '../../../features/Core/outbound/share';
import { SectionTabs } from '../../../shared/components/atoms/SectionTabs/SectionTabs';
import { applySubHeaderHeadroom } from '@services/MarppleShop/renderApp/components/cells/Header/libs/helpers/headroom.ts';
import { Header } from '@services/MarppleShop/renderApp/components/cells/Header/Global/Header.ts';
import { TabListView } from '@services/MarppleShop/shared/components/cells/TabList/TabListView.ts';

type Category = {
  id: number;
  name: string;
  count: number;
};

export type CurationListPageData = {
  current_page: number;
  total_page: number;
  total_count: number;
  categories: Category[];
  selected_category_id: string;
  curations: CurationListItem[];
  recent_product?: ProductCardViewData;
  layout_options: BaseLayoutOptions;
};

export class CurationListPage extends Page<CurationListPageData> {
  private readonly categoryTabViews: SectionTabs;

  constructor(data: CurationListPageData) {
    super(data);

    const { selected_category_id, total_count, categories } = data;

    this.categoryTabViews = new SectionTabs({
      size: this.data.layout_options.is_mobile ? 'xSmall' : 'small',
      current_tab_id: selected_category_id,
      tabs: [
        { id: 'all', label: `All Curation ${total_count}`, href: '/@/curations' },
        ...categories.map((category) => ({
          id: category.id.toString(),
          href: `/@/curations?category=${category.id}`,
          label: category.name,
          count: category.count,
        })),
      ],
      custom_klass: klass.section_tabs,
      variants: this.data.layout_options.is_mobile ? 'bubble' : undefined,
    });
  }

  protected override template(data: CurationListPageData): Html {
    const { categories, selected_category_id, curations, current_page, total_page, layout_options } = data;
    const { is_mobile } = layout_options;

    const selected_category = categories.find((category) => category.id == +selected_category_id);

    const body = html` <div class="${klass.page_body}">
      <!--   타이틀   -->
      ${is_mobile
        ? new TabListView({
            tabs: [
              {
                tab_title: ET('mps2::section_title::section_tabs::popup_store'),
                href: `/${ET.lang}/@/popup-store`,
                selected: false,
              },
              {
                tab_title: ET('mps2::section_title::section_tabs::curation'),
                href: `/${ET.lang}/@/curations`,
                selected: true,
              },
              {
                tab_title: ET('mps2::section_title::section_tabs::creator_view'),
                href: `/${ET.lang}/@/creator_views`,
                selected: false,
              },
            ],
          })
        : html`<div class="${klass.title}">${CoreUiS.makePopupStoreTitle(is_mobile, 'curation')}</div>`}

      <!--   카테고리   -->
      <div class="${klass.category}">${this.categoryTabViews}</div>

      <div class="${klass.curation_list}">
        ${curations.map(
          (curation) =>
            new CurationListItemView(
              { curation },
              {
                thumbnail_type: is_mobile ? 'short' : 'long',
                link_with_category: !!selected_category,
                is_mobile,
              },
            ),
        )}
      </div>

      <div class="${klass.pagination_container}">
        ${total_page > 1
          ? new Pagination({
              page: current_page,
              size: is_mobile ? 5 : 10,
              total: total_page,
            })
          : ''}
      </div>
    </div>`;

    return html`
      <div>
        ${new LayoutView(
          {
            body,
            footer_data: { ...footer_links, lang: ET.lang, is_mobile },
          },
          {
            full_width: is_mobile,
            floating_box: {
              recent_product: FloatingButton_recent_product.makeProps(this.data.recent_product),
              async renderHook(view: FloatingBox) {
                await FloatingBox.refreshRecentProductFloatingButton(view);
              },
            },
            ...layout_options,
            selected_bottom_tab_idx: 2,
            has_bottom_tab_bar: true,
          },
        )}
      </div>
    `;
  }

  override onMount() {
    super.onMount();

    this.delegate(PaginationChangeEvent, Pagination, function handleChangePage(e) {
      location.href = updateUrlParams(location.href, { page: e.detail.page });
    });

    if (this.data.layout_options.is_mobile) {
      applySubHeaderHeadroom(Header, this.categoryTabViews);
    }
  }
}
